export const ELLIPSIS = '…';

export const getFont = (node: HTMLElement) => {
	const style = window.getComputedStyle(node);
	return `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
};

export const getLetterSpacing = (node: HTMLElement): number => {
	const value = parseFloat(window.getComputedStyle(node).letterSpacing);
	return Number.isNaN(value) ? 0 : value;
};

let sharedMeasureContext: CanvasRenderingContext2D | null | undefined;

// Never drawn or attached to the DOM, so it's safe to create once and reuse.
export const createMeasureContext = () => {
	if (sharedMeasureContext === undefined) {
		const canvas = document.createElement('canvas');
		sharedMeasureContext = canvas.getContext('2d');
	}
	return sharedMeasureContext;
};

// Canvas measurement uses fallback metrics until the real font finishes
// loading, so an initial measurement can be permanently wrong without this.
export const whenFontsReady = (callback: () => void) => {
	if (typeof document === 'undefined' || !document.fonts) return;
	document.fonts.ready.then(callback);
};

export const measureWidth = (
	ctx: CanvasRenderingContext2D,
	text: string,
	letterSpacing: number
) => ctx.measureText(text).width + letterSpacing * text.length;

const ROUNDING_TOLERANCE_PX = 1;

// Trims one char at a time from whichever half is longer, so both sides
// shrink toward the center with exactly one ellipsis inserted.
export const fitMiddle = (
	ctx: CanvasRenderingContext2D,
	text: string,
	maxWidth: number,
	letterSpacing = 0
) => {
	const tolerantMaxWidth = maxWidth + ROUNDING_TOLERANCE_PX;

	if (measureWidth(ctx, text, letterSpacing) <= tolerantMaxWidth) {
		return text;
	}

	const splitAt = Math.ceil(text.length / 2);
	let head = text.slice(0, splitAt);
	let tail = text.slice(splitAt);

	while (
		(head.length > 0 || tail.length > 0) &&
		measureWidth(ctx, `${head}${ELLIPSIS}${tail}`, letterSpacing) >
			tolerantMaxWidth
	) {
		if (head.length >= tail.length && head.length > 0) {
			head = head.slice(0, -1);
		} else if (tail.length > 0) {
			tail = tail.slice(1);
		} else {
			break;
		}
	}

	return head || tail ? `${head}${ELLIPSIS}${tail}` : ELLIPSIS;
};
